import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLanguage } from "../i18n";

const initialForm = {
  email: "",
  name: "",
  subject: "",
  phone: "",
  message: "",
};

const validate = (values) => {
  const errors = {};
  if (!values.name.trim()) errors.name = "Please enter your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = "Please enter a valid email address.";
  }
  if (!/^[+\d][\d\s()-]{7,17}$/.test(values.phone)) {
    errors.phone = "Please enter a valid phone number.";
  }
  if (!values.subject.trim()) errors.subject = "Please enter a subject.";
  if (!values.message.trim()) errors.message = "Please enter your requirements.";
  return errors;
};

function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = ({ target: { name, value } }) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY,
          from_name: "Pranesh Enterprises Website",
          subject: `Website enquiry: ${form.subject}`,
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message,
        }),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to send enquiry.");
      }

      toast.success("Your enquiry has been sent successfully.");
      setForm(initialForm);
      setErrors({});
    } catch (error) {
      toast.error(error.message || "Unable to send your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <motion.div
        className="contact-page-wrapper"
        initial={{ y: 35, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true }}
      >
        <p className="eyebrow">{t.contactEyebrow}</p>
        <h2 className="contact-primary-heading">
          {t.contactTitle}
        </h2>
        <p className="section-intro">
          {t.contactIntro}
        </p>

        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          <h3>{t.quote}</h3>
          <div className="form-grid">
            <FormField label={t.name} name="name" value={form.name} error={errors.name} onChange={handleChange} />
            <FormField label={t.email} name="email" type="email" value={form.email} error={errors.email} onChange={handleChange} />
            <FormField label={t.phone} name="phone" type="tel" value={form.phone} error={errors.phone} onChange={handleChange} />
            <FormField label={t.subject} name="subject" value={form.subject} error={errors.subject} onChange={handleChange} />
          </div>
          <label className="form-field">
            <span>{t.requirements}</span>
            <textarea
              name="message"
              rows="5"
              value={form.message}
              onChange={handleChange}
              aria-invalid={Boolean(errors.message)}
            />
            {errors.message && <small role="alert">{errors.message}</small>}
          </label>
          <button
            className="secondary-button submit-button"
            type="submit"
            disabled={submitting}
          >
            {submitting ? t.sending : t.send}
          </button>
        </form>
      </motion.div>
    </section>
  );
}

function FormField({ label, error, ...inputProps }) {
  return (
    <label className="form-field">
      <span>{label}</span>
      <input {...inputProps} aria-invalid={Boolean(error)} />
      {error && <small role="alert">{error}</small>}
    </label>
  );
}

export default Contact;
